import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store";
import useCustomStyles from "../../hooks/useCustomStyles";
import { accessCodesStyles } from "../../assets/styles/accessCodesStyles";
import { selectState } from "../../store/slices/themeSlice";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelectInput";
import { toast } from "react-toastify";
import {
  AccessCodeType,
  fetchAccessCodesForBusiness,
  getBusinesses,
  isAccessCodeSendable,
  sendClientInvitation,
} from "../../store/actions/accessCodeActions";

type Option = { id: string; name: string };

const languageOptions: Option[] = [
  { id: "en", name: "English" },
  { id: "fr", name: "French" },
];

// B06 = invitation email template (A00-A06 / B00-B06 map to different use
// cases; this form only ever sends invitations).
const EMAIL_CODE = "B06";

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

/** Human label for a code option: id · type · remaining uses · expiry. */
function codeOptionLabel(code: AccessCodeType): string {
  const parts: string[] = [code.id, code.codeType];
  const limit = code.useLimit ?? 0;
  if (limit > 0) parts.push(`${limit - (code.useCount ?? 0)} left`);
  if (code.expire_at) {
    const d = new Date(code.expire_at);
    if (!Number.isNaN(d.getTime())) parts.push(`exp ${d.toLocaleDateString()}`);
  }
  return parts.join(" · ");
}

function SendInvitation() {
  const dispatch = useDispatch<AppDispatch>();
  const { businesses } = useSelector(
    (state: RootState) => state.accessCode_slice,
  );

  const { theme } = useSelector(selectState);
  const classes = useCustomStyles(accessCodesStyles, theme);

  const [email, setEmail] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [businessId, setBusinessId] = useState<string>("");
  const [preferredLanguage, setPreferredLanguage] = useState<string>("en");
  const [loading, setLoading] = useState<boolean>(false);
  const [lastCode, setLastCode] = useState<string | null>(null);

  const [codes, setCodes] = useState<AccessCodeType[]>([]);
  const [codesLoading, setCodesLoading] = useState<boolean>(false);
  const [selectedCodeId, setSelectedCodeId] = useState<string>("");

  useEffect(() => {
    if (!businesses || businesses.length === 0) {
      dispatch(getBusinesses({ type: "" }));
    }
  }, [businesses, dispatch]);

  const companyOptions = useMemo<Option[]>(
    () =>
      (businesses || [])
        .filter((business) => business?.id && business?.name)
        .map((business) => ({ id: business.id, name: business.name })),
    [businesses],
  );

  const selectedBusiness = useMemo(
    () => (businesses || []).find((b) => b.id === businessId),
    [businesses, businessId],
  );

  // When the business changes, load the codes it owns and reset the selection.
  useEffect(() => {
    setSelectedCodeId("");
    setCodes([]);
    if (!selectedBusiness?.name) return;

    let cancelled = false;
    setCodesLoading(true);
    fetchAccessCodesForBusiness(selectedBusiness.name)
      .then((fetched) => {
        if (!cancelled) setCodes(fetched);
      })
      .catch((e) => {
        if (!cancelled) {
          toast.error(
            e instanceof Error ? e.message : "Failed to load access codes.",
          );
          setCodes([]);
        }
      })
      .finally(() => {
        if (!cancelled) setCodesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedBusiness?.id, selectedBusiness?.name]);

  const sendableCodes = useMemo(
    () => codes.filter((code) => isAccessCodeSendable(code, selectedBusiness?.id)),
    [codes, selectedBusiness?.id],
  );

  const codeOptions = useMemo<Option[]>(
    () => sendableCodes.map((code) => ({ id: code.id, name: codeOptionLabel(code) })),
    [sendableCodes],
  );

  const canSend =
    isValidEmail(email.trim()) &&
    name.trim().length > 0 &&
    !!selectedBusiness &&
    !!selectedCodeId &&
    preferredLanguage.length > 0 &&
    !loading &&
    !codesLoading;

  const handleSend = async () => {
    if (!canSend || !selectedBusiness || !selectedCodeId) {
      toast.error(
        "Enter a valid email and name, select a company, then pick an access code.",
      );
      return;
    }

    try {
      setLoading(true);
      setLastCode(null);
      const { inviteCode } = await sendClientInvitation({
        email: email.trim(),
        name: name.trim(),
        businessName: selectedBusiness.name,
        preferredLanguage,
        inviteCode: selectedCodeId,
      });
      setLastCode(inviteCode);
      toast.success(`Invitation sent to ${email.trim()}`);
      setEmail("");
      setName("");
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to send the invitation.",
      );
    } finally {
      setLoading(false);
    }
  };

  const width = window.innerWidth * 0.4;

  const codePlaceholder = !selectedBusiness
    ? "Select a company first"
    : codesLoading
      ? "Loading codes…"
      : codeOptions.length === 0
        ? "No available codes for this company"
        : "Select an access code";

  return (
    <div className={classes.container}>
      <div className={classes.tableContainer}>
        <h2 className={classes.header}>SEND INVITATION</h2>
      </div>
      <div style={{ marginBottom: "40px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(2, 1fr)`,
            gap: "16px",
            alignItems: "center",
            marginTop: "12px",
            alignContent: "center",
            width: width,
          }}
        >
          <CustomInput
            label="Email"
            name="email"
            type="email"
            value={email}
            placeholder="person@example.com"
            onChange={(event) => setEmail(event.target.value)}
          />
          <CustomInput
            label="Name"
            name="name"
            value={name}
            placeholder="Full name"
            onChange={(event) => setName(event.target.value)}
          />

          <CustomSelect
            label="Company"
            placeholder="Select company"
            name="companyCode"
            value={businessId}
            options={companyOptions}
            onChange={(event) => setBusinessId(event.target.value)}
          />
          <CustomSelect
            label="Access code"
            placeholder={codePlaceholder}
            name="inviteCode"
            value={selectedCodeId}
            options={codeOptions}
            disabled={!selectedBusiness || codesLoading || codeOptions.length === 0}
            onChange={(event) => setSelectedCodeId(event.target.value)}
          />

          <CustomSelect
            label="Language"
            placeholder="Select language"
            name="preferredLanguage"
            value={preferredLanguage}
            options={languageOptions}
            onChange={(event) => setPreferredLanguage(event.target.value)}
          />

          <CustomInput
            label="Email template code"
            name="emailCode"
            value={EMAIL_CODE}
            disabled
            onChange={() => {}}
          />
        </div>

        <p style={{ marginTop: "12px", opacity: 0.8, width }}>
          Pick one of the selected company&apos;s valid, unredeemed access codes.
          It&apos;s emailed to this address, which is also used as the client
          email.
        </p>

        {lastCode && (
          <p style={{ marginTop: "8px", width }}>
            Last invitation code sent: <strong>{lastCode}</strong>
          </p>
        )}

        <div className={classes.buttonContainer} style={{ marginTop: "14px" }}>
          <CustomButton
            title={"Send Invitation"}
            handleClick={handleSend}
            name={"send-invitation"}
            loading={loading}
            disabled={!canSend}
          />
        </div>
      </div>
    </div>
  );
}

export default SendInvitation;
